import { Upload, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import { useConfirmationWizard } from '@/features/Quote/hooks/useConfirmationWizard';
import { isFeatureActive } from '@/lib/featureFlags';
import { getEmployeeCounts, getTotalWage } from '@/services/utils';
import {
	validateEmployeeBirthDate,
	validateEmployeeContractType,
	validateEmployeeGender,
	validateEmployeeWage
} from '@/services/validators';
import type { Employee, EmployeeSummary, SettingType } from '@/types/app';
import { FeatureFlag } from '@/types/app';
import Alert from '../Alert';
import StepConfirmButton from '../StepConfirmButton';
import AddButton from './AddButton';
import EmployeeRow from './Form';
import SmartFillPrompt from './SmartFillPrompt';

interface SmartFillState {
	rowIdx: number;
	newValue: string;
	displayName: string;
	unknownCount: number;
}

interface EmployeesProps {
	setActiveSetting?: (setting: SettingType | null) => void;
	onShowImport: () => void;
	openedFromQuoteSelection?: boolean;
	showImportMode?: boolean;
	setShowImportMode?: (show: boolean) => void;
	originalEmployeesRef?: React.MutableRefObject<Employee[]>;
	confirmedSteps: ReadonlySet<SettingType>;
	markStepConfirmed: (step: SettingType) => void;
}

const Employees = ({
	setActiveSetting,
	onShowImport,
	openedFromQuoteSelection = false,
	showImportMode = false,
	setShowImportMode,
	originalEmployeesRef,
	confirmedSteps,
	markStepConfirmed
}: EmployeesProps) => {
	const { t } = useTranslation();
	const { appData, isSubmitting, premiumVarManagement, config } = useAppContext();
	const allowExtraction = isFeatureActive(config, FeatureFlag.ALLOW_EMPLOYEE_EXTRACTION);
	const nameRefs = useRef<Record<number, HTMLInputElement | null>>({});
	const [lastAddedEmployeeIdx, setLastAddedEmployeeIdx] = useState<number | null>(null);
	const [showValidation, setShowValidation] = useState(false);
	const hasAssignedIds = useRef(false);

	const employees = useMemo(
		() => appData?.premium_variables?.employees?.employee_list || [],
		[appData?.premium_variables?.employees?.employee_list]
	);
	const { getNextUnconfirmedStep } = useConfirmationWizard();
	// Next warning the user still has to confirm this session, or null when employees
	// is the terminal step and confirming should recalculate everything at once.
	const nextStep = getNextUnconfirmedStep('employees', confirmedSteps);
	const [smartFill, setSmartFill] = useState<SmartFillState | null>(null);
	const [smartFillAppliedRows, setSmartFillAppliedRows] = useState<Set<number>>(new Set());

	const CONTRACT_TYPE_LABELS: Record<string, string> = useMemo(
		() => ({
			onb: t('quotes.settings.employees.contractTypeOptions.permanent'),
			bep: t('quotes.settings.employees.contractTypeOptions.temporary')
		}),
		[t]
	);

	const handleContractTypeChange = useCallback(
		(idx: number, oldValue: string, newValue: string) => {
			if (oldValue !== '' || newValue === '') return;
			const unknownCount = employees.filter(
				(emp, i) => i !== idx && (!emp.contract_type || emp.contract_type === '')
			).length;
			if (unknownCount === 0) return;
			setSmartFill({
				rowIdx: idx,
				newValue,
				displayName: CONTRACT_TYPE_LABELS[newValue] || newValue,
				unknownCount
			});
			setSmartFillAppliedRows(new Set());
		},
		[employees, CONTRACT_TYPE_LABELS]
	);

	const handleSmartFillApply = useCallback(() => {
		if (!smartFill) return;
		const affectedIndices = new Set<number>();
		employees.forEach((emp, i) => {
			if (i !== smartFill.rowIdx && (!emp.contract_type || emp.contract_type === '')) {
				affectedIndices.add(i);
			}
		});
		premiumVarManagement.employees.bulkUpdate(
			'contract_type',
			smartFill.newValue,
			(emp) => !emp.contract_type || emp.contract_type === ''
		);
		setSmartFillAppliedRows(affectedIndices);
		setSmartFill(null);
		setTimeout(() => setSmartFillAppliedRows(new Set()), 1500);
	}, [smartFill, employees, premiumVarManagement.employees]);

	// Ensure all employees have stable IDs for consistent animations - only run once
	useEffect(() => {
		if (hasAssignedIds.current) return;

		const employeesNeedingIds = employees.some((emp) => {
			const id = emp.id;
			return !id || (typeof id === 'string' && id.trim() === '');
		});
		if (employeesNeedingIds && employees.length > 0) {
			hasAssignedIds.current = true;
			const employeesWithIds = employees.map((emp) => {
				const id = emp.id;
				if (id && typeof id === 'string' && id.trim() !== '') {
					return emp;
				}
				return {
					...emp,
					id: `employee-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
				};
			});
			premiumVarManagement.employees.replace(employeesWithIds);
		}
	}, [employees, premiumVarManagement.employees]);

	// Show validation errors when employees exist
	useEffect(() => {
		if (employees.length > 0) {
			setShowValidation(true);
		}
	}, [employees.length]);

	useEffect(() => {
		if (lastAddedEmployeeIdx !== null) {
			nameRefs.current[lastAddedEmployeeIdx]?.focus();
			setLastAddedEmployeeIdx(null);
		}
	}, [lastAddedEmployeeIdx]);

	const addEmployee = () => {
		premiumVarManagement.employees.add();
		setLastAddedEmployeeIdx(employees.length);
	};

	const handleCancelImport = async () => {
		// Restore original employees
		if (originalEmployeesRef) {
			await premiumVarManagement.employees.replace(originalEmployeesRef.current);
		}
		if (setShowImportMode) {
			setShowImportMode(false);
		}
	};

	// Validate all employees
	const areAllEmployeesValid = useMemo(() => {
		if (employees.length === 0) return false;

		return employees.every((emp) => {
			const genderError = validateEmployeeGender(emp.gender);
			const birthDateError = validateEmployeeBirthDate(emp.birth_date);
			const wageError = validateEmployeeWage(emp.wage);
			const contractError = validateEmployeeContractType(emp.contract_type);

			return !genderError && !birthDateError && !wageError && !contractError;
		});
	}, [employees]);

	// Confirm employees (optimistic green) and move on. When `target` is the next
	// unconfirmed step we advance the wizard; when it is null this was the last card,
	// so closing the panel triggers the single deferred recalculation in Settings.
	const confirmStep = (target: SettingType | null) => {
		const totalWage = Number.parseFloat(
			getTotalWage(employees as EmployeeSummary[]).replace(/\./g, '')
		);
		premiumVarManagement.employees.updatePendingTotals(
			totalWage,
			getEmployeeCounts(employees as EmployeeSummary[]).count
		);
		// Import confirmed, disable import mode
		if (setShowImportMode) {
			setShowImportMode(false);
		}
		markStepConfirmed('employees');
		setActiveSetting?.(target);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<div data-testid="employees-grid-view">
			<div>
				<div className="flex items-center">
					<h6 className="font-bold text-lg mb-2 flex items-center gap-2">
						{t('quotes.settings.employees.panelTitle')}
						{employees.length > 0 && (
							<span className="md:hidden inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-xs font-medium rounded-full bg-primary text-primary-foreground">
								{employees.length}
							</span>
						)}
					</h6>
					{allowExtraction && (
						<Button
							type="button"
							size="sm"
							className="ml-auto hidden md:inline-flex"
							onClick={onShowImport}
							iconRight={<Upload className="h-4 w-4" />}
							data-testid="employees-import-button-header"
						>
							{t('quotes.settings.employees.importButton')}
						</Button>
					)}
				</div>
				{openedFromQuoteSelection && !allowExtraction && (
					<Alert
						variant="info"
						className="my-4"
						testId="employees-info-alert"
						showIcon
						text={t('quotes.settings.employees.updateWarning')}
					/>
				)}
				<div className="font-medium text-muted-foreground whitespace-nowrap">
					{t('quotes.settings.employees.description')}
				</div>
			</div>

			<div data-testid="employees-container" className="select-none">
				<div className="md:bg-muted/50 px-0 py-0 md:py-2 md:px-3 mb-3 mt-5 rounded-md w-full">
					{/* Desktop header - hidden on mobile */}
					<div className="hidden md:grid md:grid-cols-[56px_2fr_1.5fr_1.5fr_2fr] gap-2 text-xs font-medium px-1 pt-1 pb-2 w-full">
						<div className="">{t('quotes.settings.employees.gender')}</div>
						<div className="pl-2">
							{t('quotes.settings.employees.name')}{' '}
							<span className="text-xs text-muted-foreground font-normal">
								({t('quotes.settings.employees.nameOptional')})
							</span>
						</div>
						<div className="pl-2">{t('quotes.settings.employees.birthdate')}</div>
						<div className="pl-2">{t('quotes.settings.employees.wage')}</div>
						<div className="pl-2">{t('quotes.settings.employees.contractType')}</div>
					</div>
					{employees.map((employee, idx: number) => {
						const employeeData = employee as Employee;
						const id = employeeData.id;
						const key = id && typeof id === 'string' && id.trim() !== '' ? id : `employee-${idx}`;
						return (
							<div key={key}>
								<div
									className={
										smartFillAppliedRows.has(idx)
											? 'ring-2 ring-green-500/50 rounded-md transition-all duration-500'
											: ''
									}
								>
									<EmployeeRow
										employee={employeeData}
										idx={idx}
										nameRefs={nameRefs}
										showValidation={showValidation}
										onContractTypeChange={handleContractTypeChange}
									/>
								</div>
								{smartFill?.rowIdx === idx && (
									<SmartFillPrompt
										count={smartFill.unknownCount}
										contractTypeDisplay={smartFill.displayName}
										onApply={handleSmartFillApply}
										onDismiss={() => setSmartFill(null)}
									/>
								)}
							</div>
						);
					})}
				</div>
				<div className="flex flex-col sm:flex-row gap-2 sm:items-center">
					<div className="flex gap-2 items-center w-full sm:w-auto">
						<AddButton
							onClick={addEmployee}
							count={employees.length}
							className="flex-1 sm:flex-none sm:w-auto"
						/>
						{allowExtraction && (
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="md:hidden flex-1 sm:flex-none"
								onClick={onShowImport}
								iconRight={<Upload className="h-4 w-4" />}
								data-testid="employees-import-button"
							>
								<span className="hidden sm:inline">
									{t('quotes.settings.employees.importButton')}
								</span>
								<span className="sm:hidden">{t('quotes.settings.employees.importButton')}</span>
							</Button>
						)}
					</div>
					<div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto sm:ml-auto sm:shrink-0">
						{showImportMode && (
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="w-full sm:w-auto sm:shrink-0 whitespace-nowrap"
								onClick={handleCancelImport}
								icon={<X className="h-4 w-4" />}
								data-testid="employees-cancel-import-button"
							>
								<span className="hidden sm:inline">{t('quotes.settings.buttons.cancel')}</span>
								<span className="sm:hidden">{t('quotes.settings.buttons.cancel')}</span>
							</Button>
						)}
						<StepConfirmButton
							nextStep={nextStep}
							onConfirm={confirmStep}
							disabled={isSubmitting || !areAllEmployeesValid}
							loading={isSubmitting}
							className="w-full sm:w-auto sm:shrink-0 whitespace-nowrap"
							compactConfirmLabel
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Employees;
